import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, Button, Card, Container, Paper, Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { capturePHEvents } from '../analytics/posthog.analytics';
import {
  CalendarEventDialog,
  CalendarForm,
  CalendarStyle,
  CalendarToolbar
} from '../fl-sections/@dashboard/calendar';
import Page from '../components/Page';
import { getAllCalenderEvents, updateCalenderEvent } from '../service/calenderEvent.service';
import Iconify from '../fl-components/Iconify';
import useResponsive from '../hooks/useResponsive';
import { ASSIGN_CALENDAR_DATA } from '../redux/constants';
import { PH_ADD_EVENT_CLICKED } from '../utils/posthog.eventNames';
import Modal from '../components/common/Modal';

export default function NewFullPageCalender({ isStudentCalendar = false }) {
  const selectedOrg = useSelector((state) => state.org.selectedOrg);
  const orgLoading = useSelector((state) => state.org.orgLoading);
  const events = useSelector((state) => state.calendar.calendarData);
  const dispatch = useDispatch();
  const calendarRef = useRef(null);
  // const [events, setEvents] = useState([]);
  const isDesktop = useResponsive('up', 'sm');
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState(isDesktop ? 'dayGridMonth' : 'listWeek');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isOpenModal, setOpenModal] = useState(false);
  const [selectedRange, setSelectedRange] = useState({
    start: new Date(),
    end: new Date()
  });
  const { enqueueSnackbar } = useSnackbar();
  const [isEventSubmitting, setEventSubmitting] = useState(false);
  const [isDeleteEventLoading, setDeleteEventLoading] = useState(false);

  const fetchCalenderEvents = async () => {
    const calenderEvents = await getAllCalenderEvents(selectedOrg?.orgId);
    dispatch({
      type: ASSIGN_CALENDAR_DATA,
      payload: calenderEvents.results.map((re) => ({
        ...re,
        textColor: re?.textColor ?? '#FFC107',
        start: re.startDate,
        end: re.endDate
      }))
    });
  };

  useEffect(() => {
    if (!orgLoading && Object.keys(selectedOrg).length && events.length === 0)
      fetchCalenderEvents();
  }, [orgLoading]);

  useEffect(() => {
    if (selectedEventId) {
      const selectedEvent = events.find((_event) => _event.id === selectedEventId);
      setSelectedEvent(selectedEvent);
      setSelectedRange({
        start: selectedEvent.startDate,
        end: selectedEvent?.endDate ?? new Date()
      });
      return;
    }
    setSelectedEvent(null);
  }, [selectedEventId]);

  useEffect(() => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      const newView = isDesktop ? 'dayGridMonth' : 'listWeek';
      calendarApi.changeView(newView);
      setView(newView);
    }
  }, [isDesktop]);

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleChangeView = (newView) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleSelectRange = (arg) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.unselect();
    }
    setSelectedRange({ start: arg.start, end: arg.end });
    setOpenModal(true);
  };

  const handleSelectEvent = (arg) => {
    setOpenModal(true);
    setSelectedEventId(arg.event.id);
  };

  const updateEventDropOrResize = async (event) => {
    try {
      const eventData = events.find((ev) => ev.id === event.id);
      const newObj = {
        endDate: event.end,
        orgId: eventData.orgId,
        startDate: event.start,
        textColor: eventData.textColor,
        title: eventData.title,
        type: eventData.type,
        userId: eventData.userId
      };
      const res = await updateCalenderEvent(event.id, newObj);
      if (res) {
        const findEventIndex = events.findIndex((e) => e.id === res.id);
        events[findEventIndex] = { ...res, start: res.startDate, end: res.endDate };
        dispatch({
          type: ASSIGN_CALENDAR_DATA,
          payload: [...events]
        });
        enqueueSnackbar('Update success!');
      } else {
        dispatch({
          type: ASSIGN_CALENDAR_DATA,
          payload: [...events]
        });
        enqueueSnackbar('Please try again later', {
          variant: 'error'
        });
      }
    } catch (error) {
      dispatch({
        type: ASSIGN_CALENDAR_DATA,
        payload: [...events]
      });
      enqueueSnackbar('Something went wrong, Please try again later', {
        variant: 'error'
      });
    }
  };

  const handleResizeEvent = async ({ event }) => {
    updateEventDropOrResize(event);
  };

  const handleDropEvent = async ({ event }) => {
    updateEventDropOrResize(event);
  };

  const handleAddEvent = () => {
    capturePHEvents(PH_ADD_EVENT_CLICKED, { orgId: selectedOrg?.orgId ?? selectedOrg?.id });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEventId('');
    setSelectedEvent(null);
  };

  return (
    <Page title="Calender">
      <Container>
        {!isStudentCalendar && (
          <Stack justifyContent="end" alignItems="end" marginBottom="20px">
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" width={20} height={20} />}
              onClick={handleAddEvent}
            >
              New Event
            </Button>
          </Stack>
        )}
        <Card>
          <CalendarStyle>
            <CalendarToolbar
              date={date}
              view={view}
              onNextDate={handleClickDateNext}
              onPrevDate={handleClickDatePrev}
              onToday={handleClickToday}
              onChangeView={handleChangeView}
            />
            <FullCalendar
              weekends
              editable
              droppable
              selectable
              events={events}
              ref={calendarRef}
              rerenderDelay={10}
              initialDate={date}
              initialView={view}
              dayMaxEventRows={3}
              eventDisplay="block"
              headerToolbar={false}
              allDayMaintainDuration
              eventResizableFromStart
              select={handleSelectRange}
              eventDrop={handleDropEvent}
              eventClick={handleSelectEvent}
              eventResize={handleResizeEvent}
              height={isDesktop ? 720 : 'auto'}
              plugins={
                isStudentCalendar
                  ? [dayGridPlugin, listPlugin, timelinePlugin, timeGridPlugin]
                  : [listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]
              }
            />
          </CalendarStyle>
        </Card>
        {isStudentCalendar ? (
          <CalendarEventDialog
            open={isOpenModal}
            handleClose={handleCloseModal}
            event={selectedEvent}
          />
        ) : (
          <Modal
            open={isOpenModal}
            onClose={handleCloseModal}
            title={selectedEvent ? 'Edit Event' : 'Add Event'}
            onAction={() => document.getElementById('eventSubmitButton').click()}
            actionText={selectedEvent ? 'Update' : 'Add'}
            cancelText="Cancel"
            onCancel={() => handleCloseModal()}
            isCancelDisabled={isEventSubmitting || isDeleteEventLoading}
            isBtnLoading={isEventSubmitting}
            isActionDisabled={isDeleteEventLoading}
            PaperComponent={(props) => (
              <Box
                sx={{
                  width: '100%',
                  // height: '100%',
                  display: { xs: 'unset', md: 'flex' },
                  alignItems: { xs: 'unset', md: 'center' },
                  justifyContent: { xs: 'unset', md: 'center' }
                }}
              >
                <Box onClick={handleCloseModal} />
                <Paper {...props}>{props.children}</Paper>
              </Box>
            )}
          >
            <CalendarForm
              event={selectedEvent || {}}
              range={selectedRange}
              setEventSubmitting={setEventSubmitting}
              isDeleteLoading={isDeleteEventLoading}
              isEventSubmitting={isEventSubmitting}
              setDeleteEventLoading={setDeleteEventLoading}
              onCancel={handleCloseModal}
              onAddSuccess={(r) => {
                dispatch({
                  type: ASSIGN_CALENDAR_DATA,
                  payload: [...events, r]
                });
              }}
              onUpdateSuccess={(r) => {
                const findEventIndex = events.findIndex((e) => e.id === r.id);
                events[findEventIndex] = r;
                dispatch({
                  type: ASSIGN_CALENDAR_DATA,
                  payload: [...events]
                });
              }}
              onDeleteSuccess={(id) =>
                dispatch({
                  type: ASSIGN_CALENDAR_DATA,
                  payload: events.filter((pe) => pe.id !== id)
                })
              }
            />
          </Modal>
        )}
      </Container>
    </Page>
  );
}

NewFullPageCalender.propTypes = {
  isStudentCalendar: PropTypes.bool,
  children: PropTypes.node
};
